import React, { useState } from "react";
import {
  Animated,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import Tooltip from "../../../Components/Tooltip";
import { useDevice } from "../../../_layout";
import {
  CLIENT_NUTRITION_STATUS_ACTIVE,
} from "../../../Firestore/nutrition/nutritionPaths";
import { getClientNutritionPendingPlanStatus } from "./clientNutritionBrowserUtils";
import ClientNutritionPlanRowCard from "./ClientNutritionPlanRowCard";
import useChevronRotation from "../../../hooks/useChevronRotation";

const styles = StyleSheet.create({
  folderSection: {
    borderRadius: 12,
    borderColor: "transparent",
    borderWidth: 1,
    paddingHorizontal: 8,
    transition: "background-color 160ms ease, border-color 160ms ease",
  },
  folderSectionDropTargetValid: {
    backgroundColor: "rgba(63, 123, 141, 0.06)",
    borderColor: "rgba(63, 123, 141, 0.4)",
    borderStyle: "dashed",
  },
  folderSectionSource: {
    opacity: 0.56,
  },
  folderHeader: {
    alignItems: "center",
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "space-between",
    paddingHorizontal: 0,
    paddingVertical: 12,
  },
  folderHeaderLeft: {
    alignItems: "center",
    columnGap: 8,
    flexBasis: 220,
    flexGrow: 1,
    flexDirection: "row",
    flexShrink: 1,
    flexWrap: "wrap",
    minWidth: 0,
    rowGap: 6,
  },
  folderHeaderRight: {
    alignItems: "center",
    columnGap: 8,
    flexDirection: "row",
    flexShrink: 0,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    marginLeft: "auto",
    maxWidth: "100%",
    rowGap: 6,
  },
  folderIconButton: {
    alignItems: "center",
    justifyContent: "center",
    padding: 4,
  },
  folderIconButtonHovered: {
    opacity: 0.6,
  },
  folderTextActionButton: {
    alignItems: "center",
    backgroundColor: "rgba(63, 123, 141, 0.04)",
    borderColor: "rgba(63, 123, 141, 0.2)",
    borderRadius: 9,
    borderWidth: 1,
    flexDirection: "row",
    gap: 4,
    justifyContent: "center",
    minHeight: 28,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  folderTextActionButtonHovered: {
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    borderColor: "rgba(63, 123, 141, 0.3)",
  },
  folderTextActionText: {
    color: Colors.bubbleColor,
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 16,
  },
  folderChevronWrap: {
    alignItems: "center",
    height: 24,
    justifyContent: "center",
    width: 24,
  },
  folderTitle: {
    color: Colors.blackColor,
    fontSize: 14,
    fontWeight: "400",
    letterSpacing: 0.2,
    lineHeight: 20,
  },
  folderStateMeta: {
    alignItems: "center",
    flexDirection: "row",
    gap: 4,
    minWidth: 0,
  },
  folderStateMetaActive: {
    color: Colors.bubbleColor,
  },
  folderStateMetaArchived: {
    color: Colors.grayColor,
  },
  folderStateMetaText: {
    fontSize: 10,
    fontWeight: "500",
    lineHeight: 12,
  },
  folderContent: {
    gap: 10,
    paddingBottom: 14,
    paddingTop: 4,
  },
  folderCardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  folderEmptyText: {
    alignSelf: "center",
    color: Colors.grayColor,
    fontSize: 12,
    lineHeight: 18,
    paddingHorizontal: 12,
    paddingVertical: 32,
    textAlign: "center",
  },
});

export default function ClientNutritionFolderSection({
  section,
  pendingStatusesByPlanId = {},
  planConflictSummariesById = {},
  isExpanded,
  onToggleExpand,
  onCreatePlan,
  onExportActivePlansPdf,
  onImportFromLibrary,
  onPressPlan,
  onMovePlan,
  menuItems,
  onMenuAction,
  dragDisabled = false,
  activeDragPlanId = null,
  activeDragSourceFolderKey = null,
  activeDragOverFolderKey = null,
  pressSuppressedPlanId = null,
}) {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [isImportHovered, setIsImportHovered] = useState(false);
  const [isExportPdfHovered, setIsExportPdfHovered] = useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isChevronHovered, setIsChevronHovered] = useState(false);
  const droppableFolderId = String(section?.folderKey ?? "").trim() || "__active__";
  const { setNodeRef: setFolderDropRef } = useDroppable({
    id: `nutrition-plan-folder-drop-${droppableFolderId}`,
    data: {
      type: "nutritionPlanFolderDropTarget",
      folderKey: section?.folderKey ?? "",
    },
    disabled: dragDisabled,
  });

  const isDraggingAnyPlan = activeDragPlanId != null;
  const isFolderHoveredTarget =
    isDraggingAnyPlan && activeDragOverFolderKey === section.folderKey;
  const isValidFolderTarget =
    isFolderHoveredTarget && activeDragSourceFolderKey !== section.folderKey;
  const isSourceFolder =
    isDraggingAnyPlan && activeDragSourceFolderKey === section.folderKey;
  const chevronRotation = useChevronRotation(isExpanded);
  const isActiveFolder = section.folderKey === CLIENT_NUTRITION_STATUS_ACTIVE;
  const hasFolderPlans = Array.isArray(section?.plans) && section.plans.length > 0;
  const folderStateLabel = isActiveFolder
    ? t("clientNutritionPlanVisibleChip-string", "Visible to athlete")
    : t("clientNutritionPlanHiddenChip-string", "Hidden from athlete");

  return (
    <View
      ref={setFolderDropRef}
      style={[
        styles.folderSection,
        isValidFolderTarget ? styles.folderSectionDropTargetValid : null,
        isSourceFolder ? styles.folderSectionSource : null,
      ]}
    >
      <TouchableOpacity
        activeOpacity={1}
        disabled={isDraggingAnyPlan}
        onPress={() => onToggleExpand?.(section.folderKey)}
        style={styles.folderHeader}
      >
        <View style={styles.folderHeaderLeft}>
          <Ionicons name="folder-outline" size={20} color={Colors.grayColor} />
          <Text numberOfLines={1} style={styles.folderTitle}>
            {section.title}
          </Text>
          <View
            style={[
              styles.folderStateMeta,
            ]}
          >
            <Feather
              name={isActiveFolder ? "eye" : "eye-off"}
              size={12}
              color={isActiveFolder ? Colors.bubbleColor : Colors.grayColor}
            />
            <Text
              style={[
                styles.folderStateMetaText,
                isActiveFolder
                  ? styles.folderStateMetaActive
                  : styles.folderStateMetaArchived,
              ]}
            >
              {folderStateLabel}
            </Text>
          </View>
        </View>

        <View style={styles.folderHeaderRight}>
          <TouchableOpacity
            activeOpacity={0.7}
            onMouseEnter={() => !isMobile && setIsAddHovered(true)}
            onMouseLeave={() => setIsAddHovered(false)}
            onPress={(event) => {
              event?.stopPropagation?.();
              onCreatePlan?.(section.folderKey);
            }}
            style={[
              styles.folderTextActionButton,
              isAddHovered ? styles.folderTextActionButtonHovered : null,
            ]}
          >
            <Ionicons name="add" size={14} color={Colors.bubbleColor} />
            <Text style={styles.folderTextActionText}>
              {t("newNutritionPlanCta-string", "New nutrition plan")}
            </Text>
          </TouchableOpacity>

          <Tooltip content="importFromLibrary-string" placement="top">
            <TouchableOpacity
              activeOpacity={0.7}
              onMouseEnter={() => !isMobile && setIsImportHovered(true)}
              onMouseLeave={() => setIsImportHovered(false)}
              onPress={(event) => {
                event?.stopPropagation?.();
                onImportFromLibrary?.(section.folderKey);
              }}
              style={[
                styles.folderIconButton,
                isImportHovered ? styles.folderIconButtonHovered : null,
              ]}
            >
              <Ionicons
                name="download-outline"
                size={15}
                color={Colors.darkgrayColor}
              />
            </TouchableOpacity>
          </Tooltip>

          {isActiveFolder && hasFolderPlans ? (
            <Tooltip
              content="clientNutritionPlanFoldersExportPdfTooltip-string"
              placement="top"
            >
              <TouchableOpacity
                activeOpacity={0.7}
                onMouseEnter={() => !isMobile && setIsExportPdfHovered(true)}
                onMouseLeave={() => setIsExportPdfHovered(false)}
                onPress={(event) => {
                  event?.stopPropagation?.();
                  onExportActivePlansPdf?.(section.plans);
                }}
                style={[
                  styles.folderIconButton,
                  isExportPdfHovered ? styles.folderIconButtonHovered : null,
                ]}
              >
                <Ionicons
                  name="print-outline"
                  size={15}
                  color={Colors.darkgrayColor}
                />
              </TouchableOpacity>
            </Tooltip>
          ) : null}

          <Animated.View
            onMouseEnter={() => !isMobile && setIsChevronHovered(true)}
            onMouseLeave={() => setIsChevronHovered(false)}
            style={[
              styles.folderChevronWrap,
              isChevronHovered ? styles.folderIconButtonHovered : null,
              { transform: [{ rotate: chevronRotation }] },
            ]}
          >
            <Ionicons
              name="chevron-down"
              size={16}
              color={Colors.darkgrayColor}
            />
          </Animated.View>
        </View>
      </TouchableOpacity>

      {isExpanded ? (
        <View style={styles.folderContent}>
          {section.plans.length === 0 ? (
            <Text style={styles.folderEmptyText}>{section.emptyText}</Text>
          ) : (
            <View style={styles.folderCardsGrid}>
              {section.plans.map((plan) => {
                const pendingStatus = getClientNutritionPendingPlanStatus(
                  pendingStatusesByPlanId,
                  plan?.id,
                );

                return (
                  <ClientNutritionPlanRowCard
                    key={plan.id}
                    folderKey={section.folderKey}
                    plan={plan}
                    conflictSummary={
                      planConflictSummariesById[
                        String(plan?.id ?? "").trim().toLowerCase()
                      ] ?? null
                    }
                    effectiveStatus={pendingStatus ?? plan?.status}
                    isMovePending={pendingStatus != null}
                    menuItems={menuItems}
                    onPress={onPressPlan}
                    onMovePlan={onMovePlan}
                    onMenuAction={onMenuAction}
                    dragDisabled={dragDisabled}
                    suppressPress={
                      pressSuppressedPlanId === plan.id || activeDragPlanId === plan.id
                    }
                    isDropZoneActive={false}
                  />
                );
              })}
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
}
